/**
 * Browser play session: builds the simulation, runs it at the fixed 120 Hz step with a render-rate
 * accumulator, routes devices to players (PlayerInputManager-style joining), and draws scene + HUD.
 */
import { loadGameData } from '../../data/gameData.ts';
import type { LevelDoc } from '../../model/format.ts';
import type { Camera } from '../../render/renderer.ts';
import { buildLevel } from '../sim/build.ts';
import { emptyInput, type Game, type PlayerInput } from '../sim/game.ts';
import { applyModeSnap, captureSnap, fromGameEvent, fromNetInput, occupiedSlots, placeBody, placePlayer, toGameEvent, type NetPlay } from '../net.ts';
import type { NetBody, NetEvent, NetInput, NetPlayer } from '../../lobby/protocol.ts';
import { Coin } from '../sim/mechanics/collectables.ts';
import { applyStringLinks, cutAllStrings, presentPlayerStrings } from '../sim/mechanics/string.ts';
import { PlayerController, SOUND_LOOP } from '../sim/player.ts';
import { FIXED_DT, type V2 } from '../sim/unity.ts';
import { GameAudio } from './audio.ts';
import { drawHud, drawPause, type PauseHit } from './hud.ts';
import { pauseEntries, pauseRoot, pauseTitle, type PauseEntry } from './pauseMenu.ts';
import { GamepadDevice, KEY_SCHEMES, KeyboardDevice, TouchDevice, type InputDevice } from './input.ts';
import { browserPixelSource } from './pixels.ts';
import { GameRenderer } from './renderer.ts';
import { TouchControls } from './touch.ts';

export interface SessionOptions {
  canvas: HTMLCanvasElement;
  doc: LevelDoc;
  spawn?: [number, number];
  onExit?: () => void;
  /** set for a lobby. The host simulates; guests only draw snapshots. */
  net?: NetPlay;
}

const MAX_PLAYERS = 4;
/** TimeManager "Maximum Allowed Timestep" (1 s) */
const MAX_DELTA = 1;
const MAX_STEPS_PER_FRAME = Math.round(MAX_DELTA / FIXED_DT);
/** CameraAspectClamper limits */
const MIN_ASPECT = 3 / 4, MAX_ASPECT = 21 / 9;
/** lets the level complete jingle finish before the victory track */
const VICTORY_MUSIC_DELAY_MS = 2500;

/** CameraAspectClamper: pillarbox above 21:9, letterbox below 3:4 */
function mergeInput(a: PlayerInput, b: PlayerInput): PlayerInput {
  return {
    ...a,
    movement: Math.hypot(b.movement.x, b.movement.y) > Math.hypot(a.movement.x, a.movement.y) ? b.movement : a.movement,
    control: Math.hypot(b.control.x, b.control.y) > Math.hypot(a.control.x, a.control.y) ? b.control : a.control,
    jumpPressed: a.jumpPressed || b.jumpPressed,
    jumpReleased: a.jumpReleased || b.jumpReleased,
    dashPressed: a.dashPressed || b.dashPressed,
    interactPressed: a.interactPressed || b.interactPressed,
    interactDown: a.interactDown || b.interactDown,
    powerupPressed: a.powerupPressed || b.powerupPressed,
    powerupReleased: a.powerupReleased || b.powerupReleased,
    restartPressed: a.restartPressed || b.restartPressed,
    claimCameraPressed: a.claimCameraPressed || b.claimCameraPressed,
    controlEnabled: a.controlEnabled || b.controlEnabled,
    leftTrigger: Math.max(a.leftTrigger, b.leftTrigger),
    rightTrigger: Math.max(a.rightTrigger, b.rightTrigger),
    dUp: a.dUp || b.dUp,
    dDown: a.dDown || b.dDown,
    dLeft: a.dLeft || b.dLeft,
    dRight: a.dRight || b.dRight,
    pausePressed: a.pausePressed || b.pausePressed,
  };
}

function quietMenu(input: PlayerInput): PlayerInput {
  return { ...input, pausePressed: false, jumpPressed: false, interactPressed: false, dUp: false, dDown: false };
}

function stripEdges(input: PlayerInput): PlayerInput {
  return {
    ...input,
    jumpPressed: false,
    jumpReleased: false,
    dashPressed: false,
    interactPressed: false,
    powerupPressed: false,
    powerupReleased: false,
    restartPressed: false,
    claimCameraPressed: false,
    dUp: false,
    dDown: false,
    dLeft: false,
    dRight: false,
    pausePressed: false,
  };
}

function viewport(w: number, h: number) {
  const a = w / Math.max(1, h);
  if (a > MAX_ASPECT) {
    const vw = Math.round(h * MAX_ASPECT);
    return { x: Math.floor((w - vw) / 2), y: 0, w: vw, h };
  }
  if (a < MIN_ASPECT) {
    const vh = Math.round(w / MIN_ASPECT);
    return { x: 0, y: Math.floor((h - vh) / 2), w, h: vh };
  }
  return { x: 0, y: 0, w, h };
}

export class Session {
  private stopped = false;
  private raf = 0;
  private game: Game | null = null;
  private error: string | null = null;
  private readonly renderer = new GameRenderer();
  private readonly keyboards = KEY_SCHEMES.map((s) => new KeyboardDevice(s));
  private readonly gamepads = new Map<number, GamepadDevice>();
  private readonly touch = new TouchDevice();
  private readonly touchUi: TouchControls;
  private readonly audio = new GameAudio();
  /** device per player index (null until a device claims the player) */
  private bindings: (InputDevice | null)[] = [];
  private acc = 0;
  private last = 0;
  private elapsed = 0;
  private totalCoins = 0;
  private endgameAt = -1;
  private cssW = 0;
  private cssH = 0;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly prevTouchAction: string;
  private readonly cleanup: (() => void)[] = [];
  /** lobby slot for each joined player, host and guest alike */
  private slotOfPlayer: number[] = [];
  private snapSeq = -1;
  private snapEvents: NetEvent[] = [];
  private bodySeen = new Map<string, string>();
  private lastSnapAt = 0;
  private heldRemote = new Map<number, NetInput>();
  /** lobby slot controlled by a local device */
  private deviceOfSlot = new Map<number, InputDevice>();
  private pendingDevice: InputDevice | null = null;
  private pendingFor = 0;
  private stepLocal = new Map<number, PlayerInput>();
  private trailAcc = 0;
  private trailPrev = new Map<number, { x: number; y: number }>();
  private vis = new Map<string, { x: number; y: number; a: number }>();
  private goal = new Map<string, { x: number; y: number; a: number }>();
  private shownPlayers = new Map<number, NetPlayer>();
  private shownBodies = new Map<string, NetBody>();
  private goalCam: { x: number; y: number; size: number } | null = null;
  private visCam: { x: number; y: number; size: number } | null = null;
  private pauseMenu = 'play';
  private pauseSel = 0;
  private pauseStack: string[] = [];
  private pausePending: { cmd: string; n?: number } | null = null;
  private pauseWas = false;
  private pauseKey = false;
  private menuKey: 'up' | 'down' | 'ok' | 'back' | null = null;
  private pauseButtons: PauseHit[] = [];

  constructor(private readonly opts: SessionOptions) {
    const canvas = opts.canvas;
    this.ctx = canvas.getContext('2d')!;
    this.touchUi = new TouchControls(this.touch, () => this.exit(), {
      muted: () => this.audio.muted,
      toggle: () => this.audio.setMuted(!this.audio.muted),
    });
    this.prevTouchAction = canvas.style.touchAction;
    canvas.style.touchAction = 'none';
    this.listen(window, 'keydown', (e) => this.onKey(e as KeyboardEvent, true));
    this.listen(window, 'keyup', (e) => this.onKey(e as KeyboardEvent, false));
    this.listen(window, 'blur', () => this.releaseAll());
    this.listen(document, 'visibilitychange', () => {
      this.audio.suspend(document.hidden);
      this.last = 0;
      this.releaseAll();
    });
    this.listen(canvas, 'pointerdown', (e) => this.onPointer(e as PointerEvent, 'down'));
    this.listen(canvas, 'pointermove', (e) => this.onPointer(e as PointerEvent, 'move'));
    this.listen(canvas, 'pointerup', (e) => this.onPointer(e as PointerEvent, 'up'));
    this.listen(canvas, 'pointercancel', (e) => this.onPointer(e as PointerEvent, 'up'));
    this.listen(canvas, 'contextmenu', (e) => e.preventDefault());
    canvas.focus?.();
    if (import.meta.env.DEV) (globalThis as { __playSession?: Session }).__playSession = this;
    void this.init();
    this.raf = requestAnimationFrame(this.frame);
  }

  stop() {
    if (this.stopped) return;
    this.stopped = true;
    cancelAnimationFrame(this.raf);
    this.audio.dispose();
    for (const f of this.cleanup) f();
    this.opts.canvas.style.touchAction = this.prevTouchAction;
    const g = globalThis as { __playSession?: Session };
    if (g.__playSession === this) delete g.__playSession;
  }

  /** debugging / browser tests */
  get debugGame(): Game | null {
    return this.game;
  }
  /** smoothed per-frame timings in ms */
  readonly stats = { step: 0, render: 0, frame: 0 };

  private listen(t: EventTarget, type: string, fn: (e: Event) => void) {
    const opts = type.startsWith('touch') || type === 'pointerdown' ? { passive: false } : undefined;
    t.addEventListener(type, fn, opts);
    this.cleanup.push(() => t.removeEventListener(type, fn));
  }

  private async init() {
    try {
      const gd = await loadGameData();
      if (this.stopped) return;
      this.measure();
      const game = buildLevel({ doc: this.opts.doc, gd, pixels: browserPixelSource(gd), spawn: this.opts.spawn ?? null });
      const vp = this.cssW > 0 && this.cssH > 0 ? viewport(this.cssW, this.cssH) : null;
      game.viewAspect = vp ? vp.w / vp.h : 16 / 9;
      this.totalCoins = game.behaviours.filter((b) => b instanceof Coin).length;
      if (this.opts.net) {
        for (const slot of occupiedSlots(this.opts.net.slots)) {
          if (game.joinPlayer()) this.slotOfPlayer.push(slot);
        }
        this.bindings = this.slotOfPlayer.map(() => null);
        const mine = this.opts.net.slots.indexOf(this.opts.net.self);
        if (mine >= 0) this.deviceOfSlot.set(mine, this.keyboards[0]);
        this.syncBindings();
      } else {
        game.joinPlayer();
        this.bindings = [null];
      }
      this.game = game;
      this.audio.playUiSoon('levelStart');
    } catch (e) {
      console.error(e);
      this.error = e instanceof Error ? e.message : String(e);
    }
  }

  private exit() {
    this.opts.onExit?.();
  }

  // ---------- input ----------

  private onKey(e: KeyboardEvent, down: boolean) {
    if (this.stopped) return;
    this.audio.unlock();
    if (e.code === 'Escape') {
      if (down) {
        e.preventDefault();
        this.exit();
      }
      return;
    }
    if (this.game?.isPaused) {
      if (down && !e.repeat) {
        if (e.code === 'KeyP' || e.code === 'NumpadMultiply') this.pauseKey = true;
        else if (e.code === 'ArrowUp' || e.code === 'KeyW') this.menuKey = 'up';
        else if (e.code === 'ArrowDown' || e.code === 'KeyS') this.menuKey = 'down';
        else if (e.code === 'Enter' || e.code === 'Space') this.menuKey = 'ok';
        else if (e.code === 'Backspace') this.menuKey = 'back';
      }
      e.preventDefault();
      return;
    }
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (down && (e.metaKey || e.altKey)) return;
    if (down && e.ctrlKey && e.code !== 'ControlLeft' && e.code !== 'ControlRight' && !this.keyboards.some((kb) => kb.owns(e.code))) return;
    if (e.code === 'KeyM') {
      if (down && !e.repeat) this.audio.setMuted(!this.audio.muted);
      e.preventDefault();
      return;
    }
    let handled = false;
    for (const kb of this.keyboards) {
      if (!kb.owns(e.code)) continue;
      handled = true;
      if (down) kb.keyDown(e.code);
      else kb.keyUp(e.code);
    }
    if (handled) e.preventDefault();
  }

  private onPointer(e: PointerEvent, kind: 'down' | 'move' | 'up') {
    const canvas = this.opts.canvas;
    const r = canvas.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    const touch = e.pointerType !== 'mouse';
    if (kind === 'down' && this.game?.isPaused) {
      const hit = this.pauseButtons.find((b) => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h);
      if (hit) this.activatePause(this.currentPauseEntries()[hit.index]);
      e.preventDefault();
      return;
    }
    if (kind === 'down') {
      this.audio.unlock();
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* pointer already gone */
      }
      if (this.touchUi.pointerDown(e.pointerId, x, y, touch)) {
        e.preventDefault();
        return;
      }
      if (!touch) this.keyboards[0].mouseDown(e.button);
    } else if (kind === 'move') {
      this.touchUi.pointerMove(e.pointerId, x, y);
    } else {
      this.audio.unlock();
      this.touchUi.pointerUp(e.pointerId, x, y);
    }
  }

  private releaseAll() {
    for (const kb of this.keyboards) kb.releaseAll();
    this.touchUi.releaseAll();
  }

  private devices(): InputDevice[] {
    return [...this.keyboards, ...this.gamepads.values(), this.touch];
  }

  private pollGamepads() {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    const seen = new Set<number>();
    for (const pad of pads) {
      if (!pad) continue;
      seen.add(pad.index);
      let d = this.gamepads.get(pad.index);
      if (!d) this.gamepads.set(pad.index, (d = new GamepadDevice(pad.index, pad.id)));
      d.update(pad);
    }
    for (const [i, d] of this.gamepads) if (!seen.has(i)) d.update(null);
  }

  /** a button on an unbound device claims a waiting player or joins a new one (PlayerSpawner.OnPlayerJoined) */
  private handleJoins(game: Game) {
    for (const d of this.devices()) {
      if (this.bindings.includes(d)) {
        if (d.takeCycle()) {
          const idx = this.bindings.indexOf(d);
          const next = (idx + 1) % this.bindings.length;
          const other = this.bindings[next];
          this.bindings[next] = d;
          this.bindings[idx] = other;
        } else if (d.takeSpawn() && game.players.length < MAX_PLAYERS && game.joinPlayer()) {
          const idx = this.bindings.indexOf(d);
          this.bindings[idx] = null;
          this.bindings.push(d);
          d.clearEdges();
        }
        continue;
      }
      if (!d.takeActivity()) continue;
      const free = this.bindings.indexOf(null);
      if (free >= 0) {
        this.bindings[free] = d;
        continue;
      }
      if (game.players.length >= MAX_PLAYERS) continue;
      if (game.joinPlayer()) {
        this.bindings.push(d);
        d.clearEdges();
      }
    }
  }

  private inputs(): PlayerInput[] {
    const bound = new Set(this.bindings);
    for (const d of this.devices()) if (!bound.has(d)) d.clearEdges();
    return this.bindings.map((d) => (d ? d.consume() : emptyInput()));
  }

  // ---------- frame ----------

  private measure() {
    const canvas = this.opts.canvas;
    const r = canvas.getBoundingClientRect();
    this.cssW = r.width || canvas.clientWidth || window.innerWidth;
    this.cssH = r.height || canvas.clientHeight || window.innerHeight;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(this.cssW * dpr)), h = Math.max(1, Math.round(this.cssH * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    this.touchUi.layout(this.cssW, this.cssH);
  }

  private frame = (t: number) => {
    if (this.stopped) return;
    this.raf = requestAnimationFrame(this.frame);
    const now = t / 1000;
    const dt = this.last ? Math.min(MAX_DELTA, Math.max(0, now - this.last)) : 1 / 60;
    this.last = now;
    this.elapsed += dt;
    this.measure();
    const ctx = this.ctx;
    const w = this.opts.canvas.width, h = this.opts.canvas.height;
    const game = this.game;
    const t0 = performance.now();
    try {
      if (game && !this.error) this.advance(game, dt);
    } catch (e) {
      console.error(e);
      this.error = e instanceof Error ? e.message : String(e);
    }
    const t1 = performance.now();
    if (game && !this.error) {
      const vp = viewport(w, h);
      if (vp.w !== w || vp.h !== h) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, w, h);
      }
      this.renderer.render(ctx, game, this.camera(game, vp.w, vp.h), vp.w, vp.h, vp.x, vp.y);
      const t2 = performance.now();
      const k = 0.1;
      this.stats.step += (t1 - t0 - this.stats.step) * k;
      this.stats.render += (t2 - t1 - this.stats.render) * k;
      this.stats.frame += (dt * 1000 - this.stats.frame) * k;
    } else {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = '#16181d';
      ctx.fillRect(0, 0, w, h);
    }
    const dpr = w / Math.max(1, this.cssW);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const pauseLabels = game?.isPaused ? this.currentPauseEntries() : null;
    drawHud(ctx, this.cssW, this.cssH, {
      game,
      bindings: this.bindings,
      totalCoins: this.totalCoins,
      touch: this.touchUi.visible,
      muted: this.audio.muted,
      time: this.elapsed,
      message: game?.isInEndgame ? (this.endgameAt >= 0 && this.elapsed - this.endgameAt > 1 ? 'Level complete! Jump to replay' : 'Level complete!') : null,
      error: this.error,
      maxPlayers: MAX_PLAYERS,
      names: this.opts.net ? this.slotOfPlayer.map((slot) => this.opts.net!.names[occupiedSlots(this.opts.net!.slots).indexOf(slot)] ?? null) : undefined,
      hideJoin: false,
      exitLabel: this.opts.net?.role === 'guest' ? `Watching · host ends play · M sound ${this.audio.muted ? 'off' : 'on'}` : undefined,
    });
    if (game?.isPaused && pauseLabels) {
      this.pauseButtons = drawPause(ctx, this.cssW, this.cssH, pauseTitle(this.pauseMenu, this.pausePending?.cmd ?? null), pauseLabels.map((item) => item.label), this.pauseSel);
    } else this.pauseButtons = [];
    this.touchUi.draw(ctx);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  };

  private advance(game: Game, dt: number) {
    if (this.opts.net?.role === 'guest') {
      this.guestAdvance(game, dt);
      return;
    }
    this.pollGamepads();
    for (const cmd of this.opts.net?.pullMenu?.() ?? []) this.applyMenu(cmd.cmd, cmd.n, true);
    if (game.isPaused) {
      if (this.opts.net) this.captureLocal();
      const inputs = this.opts.net ? this.netInputs(game, true) : this.inputs();
      this.injectPauseKey(inputs);
      const acted = this.navigatePause(inputs);
      if (acted) for (const input of this.stepLocal.values()) Object.assign(input, quietMenu(input));
      game.tick(acted ? inputs.map(quietMenu) : inputs);
      this.notePause(game);
      if (!game.isPaused) for (const input of this.stepLocal.values()) input.pausePressed = false;
      if (game.isPaused) {
        game.renderUpdate(0);
        this.consumeEvents(game);
        this.sendSnap(game);
        return;
      }
    } else {
      if (this.opts.net) {
        this.ensurePlayers(game);
        this.assignDevices();
        this.captureLocal();
      } else this.handleJoins(game);
    }
    const vp = viewport(this.cssW, Math.max(1, this.cssH));
    game.viewAspect = vp.w / vp.h;
    const speed = game.gameSpeed === 0.25 || game.gameSpeed === 0.5 || game.gameSpeed === 2 ? game.gameSpeed : 1;
    this.acc += dt * speed;
    let steps = 0;
    while (this.acc >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
      const inputs = this.opts.net ? this.netInputs(game, steps === 0) : this.inputs();
      if (steps === 0) this.injectPauseKey(inputs);
      if (game.isInEndgame && this.endgameAt >= 0 && this.elapsed - this.endgameAt > 1 && inputs.some((i) => i.jumpPressed)) {
        if (!this.opts.net || this.opts.net.role === 'host') game.restartLevel();
      }
      game.tick(inputs);
      this.acc -= FIXED_DT;
      steps++;
      if (game.isPaused) break;
    }
    this.notePause(game);
    if (steps === MAX_STEPS_PER_FRAME) this.acc = 0;
    game.renderUpdate(game.isPaused ? 0 : dt);
    this.consumeEvents(game);
    this.sendSnap(game);
  }

  private sendSnap(game: Game) {
    if (this.opts.net?.role === 'host' && performance.now() - this.lastSnapAt > 50) {
      this.lastSnapAt = performance.now();
      this.opts.net.onSnap?.(captureSnap(game, this.slotOfPlayer, this.snapEvents, this.bodySeen));
    }
  }

  private notePause(game: Game) {
    if (game.isPaused !== this.pauseWas) this.audio.playUi(game.isPaused ? 'uiPause' : 'uiResume');
    if (game.isPaused && !this.pauseWas) {
      this.pauseMenu = pauseRoot(game.pauseDebug);
      this.pauseSel = 0;
      this.pauseStack = [];
      this.pausePending = null;
    }
    this.pauseWas = game.isPaused;
  }

  private injectPauseKey(inputs: PlayerInput[]) {
    if (!this.pauseKey) return;
    this.pauseKey = false;
    const i = inputs[0];
    if (i) i.pausePressed = true;
  }

  private currentPauseEntries(): PauseEntry[] {
    const game = this.game;
    const player = game?.players[game.pauseSlot] as PlayerController | undefined;
    return pauseEntries(this.pauseMenu, !!player?.invulnerableToDeath);
  }

  /** True when a menu row was chosen, so that press is not also a jump. */
  private navigatePause(inputs: PlayerInput[]): boolean {
    const entries = this.currentPauseEntries();
    if (!entries.length) return false;
    let acted = false;
    const sel = this.pauseSel;
    if (this.menuKey === 'up') this.pauseSel--;
    if (this.menuKey === 'down') this.pauseSel++;
    if (this.menuKey === 'back') {
      this.popPause();
      acted = true;
    }
    if (this.menuKey === 'ok') {
      this.activatePause(entries[(this.pauseSel + entries.length) % entries.length]);
      acted = true;
    }
    this.menuKey = null;
    for (const input of inputs) {
      if (input.dUp) this.pauseSel--;
      if (input.dDown) this.pauseSel++;
      if (input.jumpPressed || input.interactPressed) {
        this.activatePause(entries[(this.pauseSel + entries.length) % entries.length]);
        acted = true;
      }
    }
    const n = entries.length;
    if (!acted && this.pauseSel !== sel) this.audio.playUi('uiMove');
    this.pauseSel = ((this.pauseSel % n) + n) % n;
    return acted;
  }

  private popPause() {
    this.audio.playUi('uiBack');
    const prev = this.pauseStack.pop();
    if (prev) this.pauseMenu = prev;
    else this.pauseMenu = pauseRoot(!!this.game?.pauseDebug);
    this.pauseSel = 0;
    this.pausePending = null;
  }

  private activatePause(entry: PauseEntry | undefined) {
    if (!entry) return;
    if (entry.back) {
      this.popPause();
      return;
    }
    if (entry.menu) {
      this.audio.playUi('uiClick');
      this.pauseStack.push(this.pauseMenu);
      this.pauseMenu = entry.menu;
      this.pauseSel = 0;
      if (entry.menu === 'confirm' && entry.cmd) this.pausePending = { cmd: entry.cmd, n: entry.n };
      return;
    }
    if (entry.cmd === 'yes' && this.pausePending) {
      this.audio.playUi('uiConfirm');
      this.applyMenu(this.pausePending.cmd, this.pausePending.n, false);
      return;
    }
    if (entry.cmd && entry.cmd !== 'resume') this.audio.playUi('uiClick');
    if (entry.cmd) this.applyMenu(entry.cmd, entry.n, false);
  }

  private applyMenu(cmd: string, n: number | undefined, fromHost: boolean) {
    const game = this.game;
    if (!game) return;
    if (this.opts.net?.role === 'guest' && !fromHost) {
      this.opts.net.onMenu?.(cmd, n);
      return;
    }
    game.applyPause(cmd, n, { onExit: () => this.exit(), onCut: () => cutAllStrings(game) });
    if (!game.isPaused) {
      this.pauseMenu = 'play';
      this.pauseStack = [];
      this.pausePending = null;
    }
  }

  /** One person's keyboard, touch, and first gamepad, merged. */
  private ensurePlayers(game: Game) {
    const net = this.opts.net;
    if (!net) return;
    for (const slot of occupiedSlots(net.slots)) {
      if (this.slotOfPlayer.includes(slot)) continue;
      if (!game.joinPlayer()) break;
      this.slotOfPlayer.push(slot);
      this.bindings.push(null);
    }
    this.syncBindings();
  }

  /** Local keyboards and gamepads each take a player slot. A new device asks the lobby for another slot. */
  private assignDevices() {
    const net = this.opts.net;
    if (!net) return;
    const owned: number[] = [];
    for (let i = 0; i < net.slots.length; i++) if (net.slots[i] === net.self) owned.push(i);
    for (const [slot, dev] of [...this.deviceOfSlot]) {
      if (!owned.includes(slot)) this.deviceOfSlot.delete(slot);
      else if (dev.takeCycle()) {
        const at = owned.indexOf(slot);
        const next = owned[(at + 1) % owned.length];
        const other = this.deviceOfSlot.get(next);
        this.deviceOfSlot.set(next, dev);
        if (other) this.deviceOfSlot.set(slot, other);
        else this.deviceOfSlot.delete(slot);
      } else if (dev.takeSpawn()) {
        net.onTake?.();
        this.pendingDevice = dev;
        this.pendingFor = 0;
      }
    }
    for (const dev of this.devices()) {
      if ([...this.deviceOfSlot.values()].includes(dev) || dev === this.pendingDevice) continue;
      if (dev === this.touch && [...this.deviceOfSlot.values()].includes(this.keyboards[0])) continue;
      if (!dev.takeActivity()) continue;
      const free = owned.find((slot) => !this.deviceOfSlot.has(slot));
      if (free !== undefined) {
        this.deviceOfSlot.set(free, dev);
        dev.clearEdges();
        continue;
      }
      net.onTake?.();
      this.pendingDevice = dev;
      this.pendingFor = 0;
    }
    if (this.pendingDevice) {
      const free = owned.find((slot) => !this.deviceOfSlot.has(slot));
      if (free !== undefined) {
        for (const [slot, dev] of [...this.deviceOfSlot]) if (dev === this.pendingDevice) this.deviceOfSlot.delete(slot);
        this.deviceOfSlot.set(free, this.pendingDevice);
        this.pendingDevice.clearEdges();
        this.pendingDevice = null;
        this.pendingFor = 0;
      } else if (++this.pendingFor > 45) {
        this.pendingDevice = null;
        this.pendingFor = 0;
      }
    }
    this.syncBindings();
  }

  private syncBindings() {
    this.bindings = this.slotOfPlayer.map((slot) => this.deviceOfSlot.get(slot) ?? null);
  }

  private captureLocal() {
    this.stepLocal.clear();
    const bound = new Set(this.deviceOfSlot.values());
    const rideTouch = bound.has(this.keyboards[0]) && !bound.has(this.touch);
    for (const dev of this.devices()) if (!bound.has(dev) && dev !== this.pendingDevice && !(rideTouch && dev === this.touch)) dev.clearEdges();
    for (const [slot, dev] of this.deviceOfSlot) {
      let input = dev.consume();
      if (dev === this.keyboards[0] && rideTouch) input = mergeInput(input, this.touch.consume());
      this.stepLocal.set(slot, input);
    }
  }

  private netInputs(game: Game, first: boolean): PlayerInput[] {
    this.heldRemote = this.opts.net?.pullInputs?.() ?? new Map();
    return game.players.map((_, index) => {
      const slot = this.slotOfPlayer[index];
      const local = this.stepLocal.get(slot);
      if (local) return first ? local : stripEdges(local);
      const remote = this.heldRemote.get(slot);
      return remote ? fromNetInput(remote) : emptyInput();
    });
  }

  private guestAdvance(game: Game, dt: number) {
    this.pollGamepads();
    this.ensurePlayers(game);
    if (!game.isPaused) this.assignDevices();
    this.captureLocal();
    if (game.isPaused) {
      const first = this.stepLocal.values().next().value;
      if (this.pauseKey && first) first.pausePressed = true;
      this.pauseKey = false;
      this.navigatePause([...this.stepLocal.values()]);
    }
    for (const [slot, input] of this.stepLocal) this.opts.net?.onInput?.(slot, input);
    // Guests hear the host's events only: joining players and strings above would play twice.
    game.events.length = 0;
    const packet = this.opts.net?.pullSnap?.() ?? null;
    if (packet && packet.seq !== this.snapSeq) {
      this.snapSeq = packet.seq;
      this.goalCam = packet.snap.cam;
      for (const player of packet.snap.players) {
        this.shownPlayers.set(player.slot, player);
        this.goal.set(`p${player.slot}`, { x: player.x, y: player.y, a: player.a });
      }
      for (const body of packet.snap.bodies) {
        if (body.tintOnly) {
          placeBody(game.entities, body, { x: body.x, y: body.y, a: body.a });
          continue;
        }
        if (body.tint) {
          const prev = this.shownBodies.get(body.id);
          this.shownBodies.set(body.id, prev ? { ...prev, tint: body.tint } : body);
        } else this.shownBodies.set(body.id, body);
        this.goal.set(body.id, { x: body.x, y: body.y, a: body.a });
      }
      for (const event of packet.snap.events) {
        const played = toGameEvent(event);
        if (played) game.events.push(played);
      }
      applyStringLinks(game, packet.snap.links);
      applyModeSnap(game, packet.snap, this.slotOfPlayer);
      this.notePause(game);
    }
    if (game.isPaused) {
      for (const [key, target] of this.goal) this.vis.set(key, { ...target });
      if (this.goalCam) this.visCam = { ...this.goalCam };
    }
    const k = 1 - Math.exp(-dt / 0.045);
    const approach = (key: string) => {
      const target = this.goal.get(key);
      if (!target) return null;
      const prev = this.vis.get(key) ?? target;
      const next = { x: prev.x + (target.x - prev.x) * k, y: prev.y + (target.y - prev.y) * k, a: prev.a + (target.a - prev.a) * k };
      this.vis.set(key, next);
      return next;
    };
    for (const [slot, sample] of this.shownPlayers) {
      const at = approach(`p${slot}`);
      const player = game.players[this.slotOfPlayer.indexOf(slot)];
      if (at && player instanceof PlayerController) placePlayer(player, sample, at);
    }
    for (const [id, sample] of this.shownBodies) {
      const at = approach(id);
      if (at) placeBody(game.entities, sample, at);
    }
    if (this.goalCam && game.camera) {
      const prev = this.visCam ?? this.goalCam;
      this.visCam = { x: prev.x + (this.goalCam.x - prev.x) * k, y: prev.y + (this.goalCam.y - prev.y) * k, size: prev.size + (this.goalCam.size - prev.size) * k };
      game.camera.pos = { x: this.visCam.x, y: this.visCam.y };
      game.camera.orthoSize = this.visCam.size;
    }
    this.sampleTrails(game, dt);
    presentPlayerStrings(game);
    // Guests do not tick physics. Animator / Oscillator still need Update so coins spin and bob.
    game.renderUpdate(game.isPaused ? 0 : dt);
    this.consumeEvents(game);
  }

  /** Guests do not simulate, so dash trails are drawn from the smoothed path while the host says the player is dashing. */
  private sampleTrails(game: Game, dt: number) {
    this.trailAcc += dt;
    let steps = Math.floor(this.trailAcc / FIXED_DT);
    this.trailAcc -= steps * FIXED_DT;
    if (steps > 8) steps = 8;
    const start = game.time;
    if (steps > 0) {
      game.time += steps * FIXED_DT;
      game.frame += steps;
    }
    for (const [slot, sample] of this.shownPlayers) {
      const player = game.players[this.slotOfPlayer.indexOf(slot)];
      if (!(player instanceof PlayerController)) continue;
      const at = this.vis.get(`p${slot}`);
      if (!sample.dash || !at || sample.hide) {
        this.trailPrev.delete(slot);
        continue;
      }
      const prev = this.trailPrev.get(slot) ?? { x: at.x, y: at.y };
      const sprite = player.go.sprite?.sprite ?? null;
      const scale = player.go.bodyScaleXY[0];
      for (let i = 1; i <= steps; i++) {
        const u = i / Math.max(1, steps);
        player.trail.push({
          x: prev.x + (at.x - prev.x) * u,
          y: prev.y + (at.y - prev.y) * u,
          t: start + i * FIXED_DT,
          sprite,
          scale,
        });
      }
      if (player.trail.length > 64) player.trail.splice(0, player.trail.length - 64);
      this.trailPrev.set(slot, { x: at.x, y: at.y });
    }
  }

  private consumeEvents(game: Game) {
    const ear: V2 = game.camera?.pos ?? game.players[0]?.go.position ?? { x: 0, y: 0 };
    for (const e of game.events) {
      if (this.opts.net?.role === 'host') this.snapEvents.push(fromGameEvent(e));
      if (e.kind === 'levelComplete') {
        this.endgameAt = this.elapsed;
        game.isInEndgame = true;
        this.audio.playUi('levelComplete');
      } else if (e.kind === 'restart') {
        this.endgameAt = -1;
        game.isInEndgame = false;
        this.audio.playUi('restart');
      } else if (e.kind === 'checkpoint') this.audio.play('checkpoint', e.x, e.y, ear);
      else if (e.kind === 'sound') this.audio.play(e.name, e.x, e.y, ear);
    }
    game.events.length = 0;
    for (const p of game.players) {
      if (!(p instanceof PlayerController) || p.go.destroyed || !p.soundLoops) continue;
      const at = p.go.position;
      for (const [name, bit] of Object.entries(SOUND_LOOP)) {
        if (p.soundLoops & bit) this.audio.loop(`${name}:${p.index}`, name, at.x, at.y, ear);
      }
    }
    this.audio.endFrame();
    if (game.isInEndgame) this.audio.music('musicVictory', VICTORY_MUSIC_DELAY_MS);
    else this.audio.music(game.players.length > 1 ? 'musicMultiplayer' : 'musicLevel');
  }

  private camera(game: Game, w: number, h: number): Camera {
    const c = game.camera;
    let x: number, y: number, size: number;
    if (c) {
      x = c.pos.x;
      y = c.pos.y;
      size = c.orthoSize;
    } else {
      const p = game.players[0]?.go.position ?? { x: 0, y: 0 };
      x = p.x;
      y = p.y;
      size = Math.max(12.5, 23 / Math.max(0.01, w / h));
    }
    const zoom = h / (2 * size);
    return { x: Math.round(x * zoom) / zoom, y: Math.round(y * zoom) / zoom, zoom };
  }
}
